/**
 * Early Decision Validation Service
 * Ensures only one Early Decision school per student
 */

import { supabase } from '../lib/supabase.js';

class EDValidationService {
  /**
   * Check if a user can set a college to Early Decision
   * @param {string} userId - The user ID
   * @param {string} collegeId - The college being updated (null for new colleges)
   * @returns {Object} { canSetED: boolean, existingED: object|null, message: string }
   */
  static async canSetToEarlyDecision(userId, collegeId = null) {
    try {
      // Query for existing ED schools for this user
      let query = supabase
        .from('college_journey_colleges')
        .select('id, name, created_at')
        .eq('user_id', userId)
        .eq('application_round', 'early_decision');

      // If updating an existing college, exclude it from the check
      if (collegeId) {
        query = query.neq('id', collegeId);
      }

      const { data: existingEDColleges, error } = await query;

      if (error) {
        console.error('Error checking ED colleges:', error);
        return {
          canSetED: false,
          existingED: null,
          message: 'Error checking Early Decision status'
        };
      }

      if (existingEDColleges && existingEDColleges.length > 0) {
        return {
          canSetED: false,
          existingED: existingEDColleges[0],
          message: `You already have ${existingEDColleges[0].name} set as Early Decision. You can only apply Early Decision to one school.`
        };
      }

      return {
        canSetED: true,
        existingED: null,
        message: 'Can set to Early Decision'
      };
    } catch (error) {
      console.error('Error in canSetToEarlyDecision:', error);
      return {
        canSetED: false,
        existingED: null,
        message: 'Error validating Early Decision status'
      };
    }
  }

  /**
   * Validate application round before saving
   * @param {string} userId - The user ID
   * @param {string} collegeId - The college ID (null for new)
   * @param {string} applicationRound - The proposed application round
   * @returns {Object} { isValid: boolean, message: string }
   */
  static async validateApplicationRound(userId, collegeId, applicationRound) {
    // Only validate if setting to Early Decision
    if (applicationRound !== 'early_decision') {
      return { isValid: true, message: 'Valid application round' };
    }

    const validation = await this.canSetToEarlyDecision(userId, collegeId);

    return {
      isValid: validation.canSetED,
      message: validation.message,
      existingED: validation.existingED
    };
  }

  /**
   * Get all ED colleges for a user (for debugging/admin)
   * @param {string} userId - The user ID
   * @returns {Array} List of ED colleges
   */
  static async getEarlyDecisionColleges(userId) {
    try {
      const { data, error } = await supabase
        .from('college_journey_colleges')
        .select('*')
        .eq('user_id', userId)
        .eq('application_round', 'early_decision')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching ED colleges:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getEarlyDecisionColleges:', error);
      return [];
    }
  }

  /**
   * Fix multiple ED schools for a user (admin function)
   * Keeps the most recent ED, converts others to EA or Regular
   * @param {string} userId - The user ID
   * @returns {Object} Result of the fix operation
   */
  static async fixMultipleEDSchools(userId) {
    try {
      const edColleges = await this.getEarlyDecisionColleges(userId);

      if (edColleges.length <= 1) {
        return {
          success: true,
          message: 'No multiple ED schools found',
          fixed: 0
        };
      }

      // Keep the first (most recent) as ED, change others
      const fixPromises = edColleges.slice(1).map(async (college, index) => {
        // Check if college has EA option available
        const { data: globalCollege } = await supabase
          .from('global_colleges')
          .select('ea_deadline')
          .eq('id', college.global_college_id)
          .single();

        const newRound = globalCollege?.ea_deadline ? 'early_action' : 'regular';

        const { error } = await supabase
          .from('college_journey_colleges')
          .update({
            application_round: newRound,
            notes: `${college.notes || ''}\n[System: Changed from ED to ${newRound} - Only one ED school allowed]`,
            updated_at: new Date().toISOString()
          })
          .eq('id', college.id);

        if (error) {
          console.error(`Error fixing college ${college.id}:`, error);
          return false;
        }

        // Also update related timeline_items
        await supabase
          .from('timeline_items')
          .update({ application_round: newRound })
          .eq('college_id', college.id);

        return true;
      });

      const results = await Promise.all(fixPromises);
      const successCount = results.filter(r => r === true).length;

      return {
        success: true,
        message: `Fixed ${successCount} of ${edColleges.length - 1} duplicate ED schools`,
        fixed: successCount,
        kept: edColleges[0].name
      };
    } catch (error) {
      console.error('Error in fixMultipleEDSchools:', error);
      return {
        success: false,
        message: 'Error fixing multiple ED schools',
        fixed: 0
      };
    }
  }
}

export default EDValidationService;